import type { ElementType, ReactNode } from 'react';
import type { To } from 'react-router';

import type { DynamicIconName } from '../../../assets/DynamicIcon/DynamicIcon';
import type { IconName } from '../../../assets/Icon/Icon';
import type { SpacingValue } from '../../../common/sizing';
import type { PreviewCardContentProps } from '../../../formatting/PreviewCard/PreviewCard';
import type { HeightProps } from '../../../utilities/shared/Height';
import type { MaxHeightProps } from '../../../utilities/shared/MaxHeight';
import type { MaxWidthProps } from '../../../utilities/shared/MaxWidth';
import type { MinHeightProps } from '../../../utilities/shared/MinHeight';
import type { MinWidthProps } from '../../../utilities/shared/MinWidth';
import type { WidthProps } from '../../../utilities/shared/Width';

export const hopsVars = {
  icon: {
    column: {
      small: 28,
      large: 36,
    },
    row: {
      small: 28,
      large: 40,
    },
  },
  padding: {
    target: {
      ends: 6,
      sides: 10,
    },
  },
  zIndex: {
    port: 4,
    breaker: 3,
    object: 2,
    span: 1,
    connection: 0,
  },
  span: {
    buffer: 80,
  },
} satisfies {
  icon: {
    column: { small: SpacingValue; large: SpacingValue };
    row: { small: SpacingValue; large: SpacingValue };
  };
  padding: { target: { ends: number; sides: number } };
  zIndex: {
    port: number;
    breaker: number;
    object: number;
    span: number;
    connection: number;
  };
  span: { buffer: SpacingValue };
};

export type HopSharedProps = {
  id: string;
  direction?: 'column' | 'row';
  /** Controlled by the parent `Hops` component; not set per-hop. */
  size?: 'small' | 'large';
} & WidthProps &
  MaxWidthProps &
  MinWidthProps &
  HeightProps &
  MaxHeightProps &
  MinHeightProps;

export type HopPreview = {
  content: ReactNode;
} & Omit<PreviewCardContentProps, 'children'>;

/** Connection breaker preview payload for the shared preview card in `Hops`. */
export type HopConnectionPreviewPayload = HopPreview & {
  kind: 'connection';
  label: string;
  icon?: IconName;
  as?: ElementType;
  to?: To;
};

/** Object hop preview payload for the shared preview card in `Hops`. */
export type HopObjectPreviewPayload = HopPreview & {
  kind: 'object';
  icon: DynamicIconName;
  label: ReactNode;
  as?: ElementType;
  to?: To;
};

export type HopsPreviewPayload = HopConnectionPreviewPayload | HopObjectPreviewPayload;
